/**
 * "You're not being heard" (#2224 D5): the rules, kept free of LiveKit and
 * React so every case can be tested on its own. `notHeardRunner.ts` feeds
 * them once a second from `VoicePathMonitor`'s packet sampler.
 *
 * Why packets, not audio level: Opus DTX keeps an unmuted silent mic sending
 * a tiny keep-alive packet every ~400 ms, so silence and a stall look the same
 * by level but not by RTP counters. The device's own verdict comes from
 * `OutageTracker.outboundVerdict()`: "zero" (no packet left in the last
 * second) or "dtx" (plainly talking, but only DTX-sized traffic leaves).
 *
 * Agreement mode exists only on staging: when the server turned call
 * diagnostics on AND the call monitor (a silent server-side agent) has spoken
 * within the last MONITOR_FRESH_MS, the server must agree before a warning
 * shows -- it sees what really arrives. On production there is no monitor, and
 * on staging a crashed monitor goes quiet, so both fall back to the device's
 * own verdict (AC28).
 *
 * Pieces:
 *  - NotHeardDetector: 3 s of "not getting out" while unmuted -> warning;
 *    1 s of flowing again -> cleared (AC20-AC25).
 *  - RecoveryLadder: restart the mic, then a full rejoin, then stop and offer
 *    "Reconnect audio" (AC26). It never runs by itself more than once per
 *    warning episode. In client-only mode a "dtx" warning stops after the
 *    mic restart (`rejoinWarranted`): only "zero" justifies a rejoin there.
 *  - Tile marks: the warned speaker's own broadcast (AC27) plus this
 *    listener's own inbound outages (AC27b).
 */

/** "Not getting out" must hold this long before the warning shows. */
export const NOT_HEARD_TRIGGER_MS = 3000;
/** Audio must flow again this long before the warning goes. */
export const NOT_HEARD_CLEAR_MS = 1000;
/** A monitor report older than this no longer counts: client-only mode. */
export const MONITOR_FRESH_MS = 3000;
/** After restarting the mic, wait this long before trying a full rejoin. */
export const RESTART_VERIFY_MS = 5000;
/** After the rejoin connects, wait this long before calling it failed. */
export const REJOIN_VERIFY_MS = 8000;
/** A rejoin that has not even dropped the old connection by now has failed. */
export const REJOIN_START_TIMEOUT_MS = 8000;

/** Data topic of the warned speaker's own "I can't be heard" signal. */
export const NOT_HEARD_TOPIC = "weco.not-heard";
/** While warned, the signal is re-sent this often. */
export const NOT_HEARD_REFRESH_MS = 5000;
/** A speaker-wide mark with no refresh for this long is dropped. */
export const NOT_HEARD_MARK_TTL_MS = 8000;
/** A listener marks a speaker whose audio has not arrived here for this long. */
export const LISTENER_MARK_MS = 3000;

/** Console prefix of every warning / recovery line (read by verification). */
export const NOT_HEARD_LOG_PREFIX = "[weco-not-heard]";

/** The device's verdict on its own outgoing audio for the latest second. */
export type OutboundVerdict = "ok" | "zero" | "dtx" | "unknown";

// ── Monitor signal ─────────────────────────────────────────────────────────

export interface MonitorTrack {
  /** The server's packet counter for this speaker increased recently. */
  packets: boolean;
  /** The server sees more than DTX-only traffic: real speech arrives. */
  speech: boolean;
}

export interface MonitorSignal {
  v: 1;
  t: number;
  tracks: Record<string, MonitorTrack>;
}

export function decodeMonitorSignal(payload: Uint8Array): MonitorSignal | null {
  try {
    const data = JSON.parse(new TextDecoder().decode(payload)) as Partial<MonitorSignal> | null;
    if (!data || data.v !== 1 || typeof data.tracks !== "object" || data.tracks === null) return null;
    const tracks: Record<string, MonitorTrack> = {};
    for (const [identity, raw] of Object.entries(data.tracks as Record<string, unknown>)) {
      const r = raw as Partial<MonitorTrack> | null;
      if (!r || typeof r.packets !== "boolean" || typeof r.speech !== "boolean") return null;
      tracks[identity] = { packets: r.packets, speech: r.speech };
    }
    return { v: 1, t: typeof data.t === "number" ? data.t : 0, tracks };
  } catch {
    return null;
  }
}

// ── Detector ───────────────────────────────────────────────────────────────

/**
 * Whether the user means to be heard, for `NotHeardSample.unmuted`. Read
 * from the mic publication's own mute flag; while there is no publication at
 * all (a drop or rejoin unpublished it, or it is still being republished) the
 * previous intent stands -- a missing track is "can't tell", never "muted".
 */
export function micIntent(previous: boolean, publication: { isMuted: boolean } | undefined): boolean {
  return publication ? !publication.isMuted : previous;
}

export interface NotHeardSample {
  /** Epoch ms. */
  at: number;
  /** Our own connection is up. While down nothing is judged (and nothing clears). */
  connected: boolean;
  /** The user means to be heard (see `micIntent`): not muted by them. */
  unmuted: boolean;
  outbound: OutboundVerdict;
  /** The server's CALL_DIAGNOSTICS_ENABLED flag. */
  diagnostics: boolean;
  /**
   * The latest monitor report and when it arrived (local clock); `track` is
   * what it said about us -- null when it listed no track for us, which means
   * the server receives nothing from us. Null when no report ever came.
   */
  monitor: { at: number; track: MonitorTrack | null } | null;
}

export type NotHeardMode = "agreement" | "client";
export type NotHeardReason = "zero" | "dtx";

export interface NotHeardState {
  warning: boolean;
  /** The warning appeared or went on this sample. */
  changed: boolean;
  mode: NotHeardMode;
  /** Why the current bad run is bad (the trigger), or null. */
  reason: NotHeardReason | null;
}

type Verdict = "bad" | "good" | "neutral";

/** Agreement mode applies only with diagnostics on and a fresh monitor report. */
export function notHeardMode(s: Pick<NotHeardSample, "at" | "diagnostics" | "monitor">): NotHeardMode {
  return s.diagnostics && s.monitor !== null && s.at - s.monitor.at <= MONITOR_FRESH_MS ? "agreement" : "client";
}

/** One sample's verdict: is our audio not getting out (bad), getting out (good), or can't tell? */
export function classifySample(s: NotHeardSample): { verdict: Verdict; mode: NotHeardMode } {
  const mode = notHeardMode(s);
  const client = s.outbound;
  if (client === "unknown") return { verdict: "neutral", mode };
  if (mode === "client") return { verdict: client === "ok" ? "good" : "bad", mode };
  // Agreement: the server has the final say on what reaches the call.
  const server = s.monitor?.track ?? { packets: false, speech: false };
  if (client === "zero") return { verdict: server.packets ? "good" : "bad", mode };
  if (client === "dtx") return { verdict: server.speech ? "good" : "bad", mode };
  // The device says it sends; clearing also needs the server to see it.
  return { verdict: server.packets ? "good" : "neutral", mode };
}

export class NotHeardDetector {
  private warning = false;
  private badSince: number | null = null;
  private goodSince: number | null = null;
  private reason: NotHeardReason | null = null;

  get isWarning(): boolean {
    return this.warning;
  }

  observe(s: NotHeardSample): NotHeardState {
    const was = this.warning;
    const mode = notHeardMode(s);
    if (!s.connected) {
      // Our own link is down: no verdict, and nothing about the mic can be
      // trusted -- a drop (including the ladder's own rejoin) unpublishes it.
      // The warning stays (a reconnect does not prove the audio is back) but
      // no run continues across it. Checked before mute for that reason.
      this.badSince = this.goodSince = null;
    } else if (!s.unmuted) {
      // Muted: they chose not to be heard (AC24).
      this.warning = false;
      this.badSince = this.goodSince = null;
      this.reason = null;
    } else {
      const { verdict } = classifySample(s);
      if (verdict === "bad") {
        this.goodSince = null;
        if (this.badSince === null) this.badSince = s.at;
        this.reason = s.outbound === "dtx" ? "dtx" : "zero";
        if (!this.warning && s.at - this.badSince >= NOT_HEARD_TRIGGER_MS) this.warning = true;
      } else if (verdict === "good") {
        this.badSince = null;
        if (!this.warning) this.reason = null;
        if (this.goodSince === null) this.goodSince = s.at;
        if (this.warning && s.at - this.goodSince >= NOT_HEARD_CLEAR_MS) {
          this.warning = false;
          this.reason = null;
        }
      } else {
        this.badSince = this.goodSince = null;
      }
    }
    return { warning: this.warning, changed: was !== this.warning, mode, reason: this.reason };
  }
}

// ── Recovery ladder ────────────────────────────────────────────────────────

export type LadderStep = "idle" | "restarting" | "rejoining" | "failed";
export type LadderAction = "restartMic" | "rejoin";

export interface LadderInput {
  at: number;
  warning: boolean;
  connected: boolean;
  /** A full rejoin is possible here (the signed-in call has one; the guest page has not). */
  canRejoin: boolean;
  /**
   * The evidence is strong enough to tear the call down and rejoin
   * (`rejoinWarranted`). Omitted = yes. When not, step 1 still runs and the
   * ladder then stops at "failed" (the "Reconnect audio" button) instead.
   */
  rejoinWarranted?: boolean;
}

/**
 * Whether a warning may escalate to step 2 (a full rejoin). In client-only
 * mode (production: no monitor to confirm) a "dtx" verdict rests on the
 * device guessing that its user is talking, so it may warn and restart the
 * mic but never drop everyone's audio for a rejoin; only "zero" (no packet
 * leaves at all) may. Agreement mode is confirmed by the server: unchanged.
 */
export function rejoinWarranted(state: Pick<NotHeardState, "mode" | "reason">): boolean {
  return state.mode === "agreement" || state.reason !== "dtx";
}

/**
 * Step 1 restart the mic; RESTART_VERIFY_MS later, if still warned, step 2 a
 * full rejoin; if the warning outlives REJOIN_VERIFY_MS after the rejoin
 * connected, stop: "failed" shows the "Reconnect audio" button. One automatic
 * run per warning episode; only `retry()` (the button) starts another.
 */
export class RecoveryLadder {
  private step: LadderStep = "idle";
  private stepAt = 0;
  private autoUsed = false;
  private sawDrop = false;
  private backAt: number | null = null;

  get current(): LadderStep {
    return this.step;
  }

  update(i: LadderInput): LadderAction | null {
    if (!i.warning) {
      // The episode is over: audio is back, or the user muted.
      this.step = "idle";
      this.autoUsed = false;
      return null;
    }
    switch (this.step) {
      case "idle":
        if (this.autoUsed) return null;
        this.autoUsed = true;
        return this.startRestart(i.at);
      case "restarting":
        if (i.at - this.stepAt < RESTART_VERIFY_MS) return null;
        if (!i.canRejoin || i.rejoinWarranted === false) {
          this.step = "failed";
          return null;
        }
        this.step = "rejoining";
        this.stepAt = i.at;
        this.sawDrop = false;
        this.backAt = null;
        return "rejoin";
      case "rejoining":
        if (!this.sawDrop) {
          if (!i.connected) this.sawDrop = true;
          else if (i.at - this.stepAt >= REJOIN_START_TIMEOUT_MS) this.step = "failed";
          return null;
        }
        if (!i.connected) {
          // Still reconnecting: the reconnect controller never gives up, so
          // neither does this step while it works.
          this.backAt = null;
          return null;
        }
        if (this.backAt === null) this.backAt = i.at;
        if (i.at - this.backAt >= REJOIN_VERIFY_MS) this.step = "failed";
        return null;
      case "failed":
        return null;
    }
  }

  /**
   * The old connection dropped (room Disconnected event). A fast rejoin can
   * drop and come back between two 1 s samples, so the event says so too.
   */
  noteDisconnected(): void {
    if (this.step === "rejoining") this.sawDrop = true;
  }

  /** The rejoin could not be started (one already running, or no session). */
  rejoinRefused(): void {
    if (this.step === "rejoining") this.step = "failed";
  }

  /** "Reconnect audio": run the ladder again from step 1. */
  retry(at: number): LadderAction | null {
    if (this.step !== "failed") return null;
    return this.startRestart(at);
  }

  private startRestart(at: number): LadderAction {
    this.step = "restarting";
    this.stepAt = at;
    return "restartMic";
  }
}

// ── Tile marks ─────────────────────────────────────────────────────────────

export interface NotHeardSignal {
  v: 1;
  warning: boolean;
}

export function encodeNotHeardSignal(warning: boolean): Uint8Array {
  return new TextEncoder().encode(JSON.stringify({ v: 1, warning } satisfies NotHeardSignal));
}

export function decodeNotHeardSignal(payload: Uint8Array): NotHeardSignal | null {
  try {
    const data = JSON.parse(new TextDecoder().decode(payload)) as Partial<NotHeardSignal> | null;
    if (!data || data.v !== 1 || typeof data.warning !== "boolean") return null;
    return { v: 1, warning: data.warning };
  } catch {
    return null;
  }
}

/** Send on every change, and every NOT_HEARD_REFRESH_MS while warned. Nothing before the first warning. */
export function shouldSendNotHeard(
  warning: boolean,
  last: { warning: boolean; at: number } | null,
  now: number,
): boolean {
  if (!last) return warning;
  if (last.warning !== warning) return true;
  return warning && now - last.at >= NOT_HEARD_REFRESH_MS;
}

/** Speaker-wide marks: what each warned speaker last said about themselves. */
export class SpeakerSignals {
  private warnedAt = new Map<string, number>();

  receive(identity: string, warning: boolean, at: number): void {
    if (warning) this.warnedAt.set(identity, at);
    else this.warnedAt.delete(identity);
  }

  forget(identity: string): void {
    this.warnedAt.delete(identity);
  }

  /** Speakers whose last "warning: true" is fresh enough. */
  active(now: number): string[] {
    const out: string[] = [];
    for (const [identity, at] of this.warnedAt) {
      if (now - at <= NOT_HEARD_MARK_TTL_MS) out.push(identity);
      else this.warnedAt.delete(identity);
    }
    return out;
  }
}

/**
 * Which tiles carry "can't be heard right now": speakers who said so
 * themselves, plus speakers whose audio has not arrived HERE for
 * LISTENER_MARK_MS (`missingSince` = the open inbound outage's start, which
 * already includes the tracker's first-packet grace). Never ourselves, and
 * never an agent (call monitor, meeting twin).
 */
export function tileMarks({
  now,
  self,
  broadcast,
  missingSince,
  excluded,
}: {
  now: number;
  self: string;
  broadcast: string[];
  missingSince: Map<string, number>;
  excluded: Set<string>;
}): string[] {
  const marks = new Set<string>(broadcast);
  for (const [identity, since] of missingSince) if (now - since >= LISTENER_MARK_MS) marks.add(identity);
  marks.delete(self);
  for (const identity of excluded) marks.delete(identity);
  return [...marks].sort();
}

// ── Console lines ──────────────────────────────────────────────────────────

export type NotHeardLogEvent =
  | "warning shown"
  | "warning cleared"
  | "step 1 restart mic"
  | "step 1 restart mic failed"
  | "step 2 full rejoin"
  | "step 2 skipped, rejoined moments ago"
  | "recovery failed, offering Reconnect audio"
  | "Reconnect audio pressed";

/** One timestamped line per event, e.g. `[weco-not-heard] 2026-11-03T10:00:00.000Z warning shown mode=client reason=zero`. */
export function notHeardLogLine(event: NotHeardLogEvent, at: number, detail?: Record<string, string>): string {
  const extra = detail ? Object.entries(detail).map(([k, v]) => ` ${k}=${v}`).join("") : "";
  return `${NOT_HEARD_LOG_PREFIX} ${new Date(at).toISOString()} ${event}${extra}`;
}
